import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Header from "../common/Header";
import { useDispatch, useSelector } from "react-redux";
import { getUserProfile, updateUserProfile } from "../../redux/reduxSlices/userProfileSlice";
import toast from "react-hot-toast";

function ProfilePage({ onLogout, onNewOrder }) {
  const [isEditing, setIsEditing] = useState(false);
  const { user, error } = useSelector((state) => state.profile);
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(getUserProfile());
  }, []);

  // Initialize react-hook-form with default values
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      user_type: "",
      first_name: "",
      last_name: "",
      email: "",
      mobile: "",
    }
  });

  // Reset form when user data is loaded or changes
  useEffect(() => {
    if (user) {
      reset({
        user_type: user?.user_type || "",
        first_name: user?.first_name || "",
        last_name: user?.last_name || "",
        email: user?.email || "",
        mobile: user?.mobile || "",
      });
    }
  }, [user, reset]); // Add reset to dependencies

  // Handler functions
  const handleLogout = () => {
    onLogout?.();
  };

  const handleNewOrder = () => {
    onNewOrder?.();
  };

  const handleEdit = () => {
    setIsEditing(true);
    // Ensure form has current user values when entering edit mode
    if (user) {
      reset({
        user_type: user?.user_type || "",
        first_name: user?.first_name || "",
        last_name: user?.last_name || "",
        email: user?.email || "",
        mobile: user?.mobile || "",
      });
    }
  };

  // Form submission handler
  const onSubmit = async (data) => {
    try {
      const payload = {
        "first_name": data.first_name,
        "last_name": data.last_name,
        "mobile": data.mobile
      }

      const response = await dispatch(updateUserProfile(payload)).unwrap()
      if (response.success) {
        await dispatch(getUserProfile())
        toast.success("Profile updated successfully!");
        setIsEditing(false);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile. Please try again.");
    }
  };

  const handleCancel = () => {
    // Reset to current user data
    if (user) {
      reset({
        user_type: user?.user_type || "",
        first_name: user?.first_name || "",
        last_name: user?.last_name || "",
        email: user?.email || "",
        mobile: user?.mobile || "",
      });
    }
    setIsEditing(false);
  };

  return (
    <div className="min-h-screen">
      <Header user={user} onLogout={handleLogout} onNewOrder={handleNewOrder} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Profile</h1>
            <p className="text-gray-600 mt-2">
              Manage your account information and preferences
            </p>
          </div>
          {!isEditing && (
            <button
              onClick={handleEdit}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
              type="button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span>Edit Profile</span>
            </button>
          )}
        </div>

        {/* Profile Form */}
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
          <div className="px-6 py-8 sm:p-8">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* User Type - ALWAYS READONLY */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    User Type
                  </label>
                  <div className="w-full border border-transparent rounded-lg px-4 py-3 bg-gray-50 text-gray-700">
                    {user?.user_type || <span className="text-gray-400">Not provided</span>}
                  </div>
                </div>

                {/* First Name */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    First Name
                  </label>
                  {isEditing ? (
                    <div className="space-y-1">
                      <input
                        type="text"
                        {...register("first_name", {
                          // required: "First name is required",
                          pattern: {
                            value: /^[a-zA-Z\s]+$/,
                            message: "First name can only contain letters and spaces"
                          },
                          minLength: {
                            value: 2,
                            message: "First name must be at least 2 characters"
                          }
                        })}
                        className={`w-full border ${errors.first_name ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200`}
                        placeholder="First name"
                        disabled={isSubmitting}
                      />
                      {errors.first_name && (
                        <p className="text-red-500 text-sm mt-1">{errors.first_name.message}</p>
                      )}
                    </div>
                  ) : (
                    <div className="w-full border border-transparent rounded-lg px-4 py-3 bg-gray-50 text-gray-700">
                      {user?.first_name || <span className="text-gray-400">Not provided</span>}
                    </div>
                  )}
                </div>

                {/* Last Name */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Last Name
                  </label>
                  {isEditing ? (
                    <div className="space-y-1">
                      <input
                        type="text"
                        {...register("last_name", {
                          // required: "Last name is required",
                          pattern: {
                            value: /^[a-zA-Z\s]+$/,
                            message: "Last name can only contain letters and spaces"
                          },
                          minLength: {
                            value: 2,
                            message: "Last name must be at least 2 characters"
                          }
                        })}
                        className={`w-full border ${errors.last_name ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200`}
                        placeholder="Last name"
                        disabled={isSubmitting}
                      />
                      {errors.last_name && (
                        <p className="text-red-500 text-sm mt-1">{errors.last_name.message}</p>
                      )}
                    </div>
                  ) : (
                    <div className="w-full border border-transparent rounded-lg px-4 py-3 bg-gray-50 text-gray-700">
                      {user?.last_name || <span className="text-gray-400">Not provided</span>}
                    </div>
                  )}
                </div>

                {/* Email - ALWAYS READONLY */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Email
                  </label>
                  <div className="w-full border border-transparent rounded-lg px-4 py-3 bg-gray-50 text-gray-700">
                    {user?.email || <span className="text-gray-400">Not provided</span>}
                  </div>
                </div>

                {/* Mobile Number */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Mobile Number
                  </label>
                  {isEditing ? (
                    <div className="space-y-1">
                      <input
                        type="tel"
                        {...register("mobile", {
                          // required: "Mobile number is required", // Add if needed
                          pattern: {
                            value: /^[0-9]{10}$/, // Exactly 10 digits, only numbers
                            message: "Mobile number must be exactly 10 digits"
                          },
                          minLength: {
                            value: 10,
                            message: "Mobile number must be exactly 10 digits"
                          },
                          maxLength: {
                            value: 10,
                            message: "Mobile number must be exactly 10 digits"
                          },
                          validate: (value) => {
                            if (!value) return true;
                            // Check if all characters are digits
                            return /^\d+$/.test(value) || "Only numbers are allowed";
                          }
                        })}
                        className={`w-full border ${errors.mobile ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200`}
                        placeholder="10 digit mobile number"
                        disabled={isSubmitting}
                        maxLength={10}
                        onInput={(e) => {
                          // Allow only numbers
                          e.target.value = e.target.value.replace(/[^0-9]/g, '');
                        }}
                      />
                      {errors.mobile && (
                        <p className="text-red-500 text-sm mt-1">{errors.mobile.message}</p>
                      )}
                    </div>
                  ) : (
                    <div className="w-full border border-transparent rounded-lg px-4 py-3 bg-gray-50 text-gray-700">
                      {user?.mobile || <span className="text-gray-400">Not provided</span>}
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons - Only show when editing */}
              {isEditing && (
                <div className="md:col-span-2 flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-3 rounded-lg font-semibold text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: "linear-gradient(135deg, #DD7246 0%, #c85a32 100%)",
                    }}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center space-x-2">
                        <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span>Updating...</span>
                      </span>
                    ) : (
                      "Update Profile"
                    )}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;